import React, { ReactNode } from 'react';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

type GridColumns = 1 | 2 | 3 | 4 | 6 | 12;

export interface ResponsiveGridProps {
  children: ReactNode;
  className?: string;
  mobileColumns?: GridColumns;
  tabletColumns?: GridColumns;
  desktopColumns?: GridColumns;
  gap?: string;
  mobileGap?: string;
  alignItems?: string;
  justifyItems?: string;
}

export const ResponsiveGrid: React.FC<ResponsiveGridProps> = ({
  children,
  className = '',
  mobileColumns = 1,
  tabletColumns = 2,
  desktopColumns = 3,
  gap = 'gap-6',
  mobileGap = 'gap-4',
  alignItems = 'items-stretch',
  justifyItems = 'justify-items-stretch'
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1024px)');

  const getGridCols = () => {
    if (isMobile) return `grid-cols-${mobileColumns}`;
    if (isTablet) return `grid-cols-${tabletColumns}`;
    return `grid-cols-${desktopColumns}`;
  };

  return (
    <div 
      className={`
        grid 
        ${getGridCols()}
        ${isMobile ? mobileGap : gap}
        ${alignItems}
        ${justifyItems}
        ${className}
      `}
    >
      {children}
    </div>
  );
};

// Preset configurations for common use cases
export const StatsGrid: React.FC<Omit<ResponsiveGridProps, 'mobileColumns' | 'tabletColumns' | 'desktopColumns'>> = (props) => (
  <ResponsiveGrid
    mobileColumns={2}
    tabletColumns={2}
    desktopColumns={4}
    {...props}
  />
);

export const CardGrid: React.FC<Omit<ResponsiveGridProps, 'mobileColumns' | 'tabletColumns' | 'desktopColumns'>> = (props) => (
  <ResponsiveGrid
    mobileColumns={1}
    tabletColumns={2}
    desktopColumns={3}
    {...props}
  />
);

export const FormGrid: React.FC<Omit<ResponsiveGridProps, 'mobileColumns' | 'tabletColumns' | 'desktopColumns'>> = (props) => (
  <ResponsiveGrid
    mobileColumns={1}
    tabletColumns={2}
    desktopColumns={2}
    gap="gap-4"
    mobileGap="gap-3"
    {...props}
  />
);

export default ResponsiveGrid;
